import { lendingApiFetchJson } from 'lendingApi/lendingApi';
import { URLS } from 'utils/tests/urls.config';

// Derive the auth-service host by swapping the leftmost label with "auth".
// www.iwoca.co.uk → auth.iwoca.co.uk, www.iwoca.de → auth.iwoca.de, and
// likewise for iwoca-dev.* — mirrors how auth-service resolves its cookie
// domain from the inbound Host header, so a single deployment serves both
// UK and DE on every environment. In dev, /auth is proxied via vite.
export const getAuthBaseUrl = (): string => {
  if (import.meta.env.DEV) {
    return '/auth';
  }
  const host = window.location.host;
  const firstDot = host.indexOf('.');
  const authHost =
    firstDot !== -1 ? `auth.${host.substring(firstDot + 1)}` : host;
  return `https://${authHost}`;
};

const HTTP_CONFLICT = 409;

const normaliseEmail = (email: string) => email.trim().toLowerCase();

export class DuplicateEmailError extends Error {}

export const isEmailAvailable = async (email: string): Promise<boolean> => {
  try {
    const response = await lendingApiFetchJson(
      URLS.emailAvailability({ email }),
    );
    return response.data.available !== false;
  } catch {
    return true;
  }
};

const signupWithCognito = async (email: string): Promise<Response | null> => {
  try {
    // eslint-disable-next-line iwoca/prefer-lapi-client
    return await fetch(`${getAuthBaseUrl()}/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });
  } catch {
    return null;
  }
};

const isSignedInToCognito = async (): Promise<string | null> => {
  try {
    // eslint-disable-next-line iwoca/prefer-lapi-client
    const response = await fetch(`${getAuthBaseUrl()}/me`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      return null;
    }
    const me: { email?: string } = await response.json();
    return me.email ?? '';
  } catch {
    return null;
  }
};

export const mintCognitoUserForSignup = async (
  email: string,
): Promise<boolean> => {
  if (!(await isEmailAvailable(email))) {
    throw new DuplicateEmailError(email);
  }
  const response = await signupWithCognito(email);
  if (!response) {
    return false;
  }
  if (response.ok) {
    return true;
  }
  if (response.status !== HTTP_CONFLICT) {
    return false;
  }
  const signedInEmail = await isSignedInToCognito();
  if (signedInEmail === null) {
    throw new DuplicateEmailError(email);
  }
  return normaliseEmail(signedInEmail) === normaliseEmail(email);
};
